import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  FACING_BEARING,
  FACING_OPTIONS,
  SIDES,
  directionsFromEdges,
  finalDirection,
  setDirectionEdit,
  setNorthAngle,
  splitNames,
  unitKeys
} from '../../utils/reraLayout';
import { Fold, plural } from './reraUi';

const CONFIDENCE = {
  clear: { label: 'One road', color: '#16a34a' },
  corner: { label: 'Corner: two roads', color: '#2563eb' },
  choose: { label: 'Roads on opposite sides: choose', color: '#f59e0b' },
  none: { label: 'No road', color: '#6b7280' }
};

const SIDE_LABEL = { north: 'North', east: 'East', south: 'South', west: 'West' };

const centre = (p, W, H) => ({
  x: (p.points.reduce((a, q) => a + q[0], 0) / p.points.length) * W,
  y: (p.points.reduce((a, q) => a + q[1], 0) / p.points.length) * H
});

/** Screen vector for a compass bearing on a plan whose north is turned by `northAngle`. */
const vec = (bearing, northAngle) => {
  const t = ((bearing + northAngle) * Math.PI) / 180;
  return { x: Math.sin(t), y: -Math.cos(t) };
};

/** A cell of comma-separated names. Keeps what is typed ("Road, ") while the draft holds the parsed list. */
const NamesCell = ({ value, onChange, label }) => {
  const joined = value.join(', ');
  const [text, setText] = useState(joined);
  useEffect(() => {
    if (splitNames(text).join(', ') !== joined) setText(joined);
  }, [joined]);
  return (
    <input
      aria-label={label}
      value={text}
      onChange={(e) => {
        setText(e.target.value);
        onChange(splitNames(e.target.value));
      }}
      className="w-full rounded-md border border-hairline px-2 py-1 text-sm focus:border-brand-600 focus:outline-none"
    />
  );
};

/**
 * The map step of the import: every plot outlined over the picture, a north control, and a
 * table of facing and what is beside each side for every unit. Everything starts from what
 * the server read off the map and can be changed before Create.
 */
const ReraMapDirections = ({ draft, setDraft }) => {
  const layout = draft.layout;
  const [selected, setSelected] = useState(null);
  const rowRefs = useRef({});

  const keys = useMemo(() => unitKeys(draft), [draft]);
  const plots = useMemo(
    () =>
      (layout?.plots || [])
        .filter((p) => keys.has(p.plotKey.toLowerCase()))
        .sort((a, b) => a.plotKey.localeCompare(b.plotKey, undefined, { numeric: true })),
    [layout, keys]
  );
  const north = layout?.northAngle ?? 0;
  const rows = useMemo(
    () =>
      plots.map((p) => {
        const derived = directionsFromEdges(p.edges, north);
        const edit = draft.directionEdits?.[p.plotKey];
        return { plot: p, derived, final: finalDirection(derived, edit), edited: Boolean(edit) };
      }),
    [plots, north, draft.directionEdits]
  );
  const counts = useMemo(() => {
    const c = { clear: 0, corner: 0, choose: 0, none: 0 };
    rows.forEach((r) => (c[r.derived.confidence] += 1));
    return c;
  }, [rows]);

  if (!layout) return null;
  const { imageWidth: W, imageHeight: H } = layout;
  const toPoints = (pts) => pts.map(([x, y]) => `${x * W},${y * H}`).join(' ');
  const select = (key) => {
    setSelected(key);
    rowRefs.current[key]?.scrollIntoView?.({ block: 'nearest' });
  };

  return (
    <Fold
      title="Map and directions"
      hint={`${plural(plots.length, 'plot', 'plots')} outlined · ${counts.clear} clear · ${counts.corner} corners${
        counts.choose ? ` · ${counts.choose} to choose` : ''
      }`}
      defaultOpen>
      <p className="mb-3 text-sm text-gray-600">
        We outlined each plot on your map and looked at what is beside each of its four sides. Turn the north control
        until the compass matches the north arrow on your plan. Facing is the side a road is on. Change anything that is
        wrong; nothing is saved until you press Create.
      </p>

      <div className="mb-3 flex flex-wrap items-center gap-4">
        <label className="flex items-center gap-2 text-sm">
          <span>
            North is turned <b>{north}°</b>
          </span>
          <input
            aria-label="North angle"
            type="range"
            min={-180}
            max={180}
            step={5}
            value={north}
            onChange={(e) => setDraft(setNorthAngle(draft, Number(e.target.value)))}
            className="w-56"
          />
        </label>
        <svg width="44" height="44" viewBox="-22 -22 44 44" aria-label="Compass">
          <circle r="20" fill="#fff" stroke="#cbd5e1" />
          <g transform={`rotate(${north})`}>
            <polygon points="0,-16 5,2 -5,2" fill="#dc2626" />
            <text y="14" textAnchor="middle" fontSize="9" fontWeight="700" fill="#0f172a">
              N
            </text>
          </g>
        </svg>
        <button
          type="button"
          className="rounded-md border border-hairline px-3 py-1 text-xs hover:bg-surface-muted"
          onClick={() => setDraft(setNorthAngle(draft, 0))}>
          Reset north
        </button>
      </div>

      <div className="mb-3 flex flex-wrap gap-2 text-xs">
        {Object.entries(CONFIDENCE).map(([key, c]) => (
          <span key={key} className="inline-flex items-center rounded-full border px-2.5 py-1" style={{ borderColor: c.color }}>
            <span className="mr-1.5 inline-block h-2.5 w-2.5 rounded-sm" style={{ background: c.color }} />
            {c.label} ({counts[key]})
          </span>
        ))}
      </div>

      <div className="relative mb-4 overflow-hidden rounded-lg border border-hairline bg-surface-muted" style={{ aspectRatio: `${W} / ${H}` }}>
        {layout.imageUrl && (
          <img src={layout.imageUrl} alt="Master plan" draggable={false} className="absolute inset-0 h-full w-full select-none" />
        )}
        <svg viewBox={`0 0 ${W} ${H}`} className="absolute inset-0 h-full w-full" data-testid="rera-map">
          {/* The ground the plots sit on, drawn from traced shapes: no picture is kept. */}
          {!layout.imageUrl && layout.layers && Object.keys(layout.layers).length > 0 && (
            <g transform={`scale(${W} ${H})`} data-testid="rera-ground">
              {layout.layers.green && <path d={layout.layers.green} fill="#b9d08a" fillRule="evenodd" />}
              {layout.layers.roads && <path d={layout.layers.roads} fill="#8b929c" fillRule="evenodd" />}
              {layout.layers.water && <path d={layout.layers.water} fill="#7cc4e0" fillRule="evenodd" />}
              {layout.layers.hall && <path d={layout.layers.hall} fill="#e8c79a" fillRule="evenodd" />}
            </g>
          )}
          {rows.map(({ plot, derived, final }) => {
            const active = plot.plotKey === selected;
            const doubtful = plot.flags.length > 0;
            return (
              <polygon
                key={plot.plotKey}
                data-testid={`outline-${plot.plotKey}`}
                points={toPoints(plot.points)}
                fill={CONFIDENCE[derived.confidence].color}
                fillOpacity={active ? 0.85 : 0.55}
                stroke={active ? '#000' : doubtful ? '#f59e0b' : '#fff'}
                strokeWidth={active ? 7 : doubtful ? 6 : 3}
                strokeDasharray={doubtful ? '14 8' : undefined}
                style={{ cursor: 'pointer' }}
                onClick={() => select(plot.plotKey)}
              />
            );
          })}
          {rows.map(({ plot, final }) => {
            if (!final.facing) return null;
            const c = centre(plot, W, H);
            const v = vec(FACING_BEARING[final.facing], north);
            const tip = { x: c.x + v.x * 42, y: c.y + v.y * 42 };
            const back = { x: c.x - v.x * 26, y: c.y - v.y * 26 };
            const nx = -v.y;
            const ny = v.x;
            const head = [
              [tip.x, tip.y],
              [tip.x - v.x * 22 + nx * 13, tip.y - v.y * 22 + ny * 13],
              [tip.x - v.x * 22 - nx * 13, tip.y - v.y * 22 - ny * 13]
            ];
            return (
              <g key={`a${plot.plotKey}`} style={{ pointerEvents: 'none' }}>
                <line x1={back.x} y1={back.y} x2={tip.x} y2={tip.y} stroke="#fff" strokeWidth="9" strokeLinecap="round" />
                <polygon points={head.map((q) => q.join(',')).join(' ')} fill="#fff" />
              </g>
            );
          })}
          {rows.map(({ plot }) => {
            const c = centre(plot, W, H);
            return (
              <text key={`t${plot.plotKey}`} x={c.x} y={c.y + 40} textAnchor="middle" fontSize="26" fontWeight="700" fill="#fff" stroke="#0f172a" strokeWidth="0.6" style={{ pointerEvents: 'none' }}>
                {plot.plotKey}
              </text>
            );
          })}
        </svg>
      </div>

      {layout.unplaced?.length > 0 && (
        <p className="mb-3 text-xs text-warning-fg">
          No outline for {layout.unplaced.slice(0, 15).join(', ')}
          {layout.unplaced.length > 15 ? ' and more' : ''}. They are still created as units, just not on the map.
        </p>
      )}

      <div className="max-h-80 overflow-auto rounded-lg border border-hairline">
        <table className="w-full min-w-[640px] text-sm">
          <thead className="sticky top-0 bg-surface-muted text-left text-xs text-gray-600">
            <tr>
              <th className="px-3 py-2">Plot</th>
              <th className="px-3 py-2">Facing</th>
              {SIDES.map((s) => (
                <th key={s} className="px-3 py-2">
                  {SIDE_LABEL[s]}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map(({ plot, derived, final, edited }) => {
              const check = !edited && (derived.confidence === 'corner' || derived.confidence === 'choose' || plot.flags.length > 0);
              return (
                <tr
                  key={plot.plotKey}
                  ref={(el) => {
                    rowRefs.current[plot.plotKey] = el;
                  }}
                  onClick={() => setSelected(plot.plotKey)}
                  className={`border-t border-hairline ${plot.plotKey === selected ? 'bg-brand-50' : check ? 'bg-warning-bg' : ''}`}>
                  <td className="px-3 py-1.5 align-top">
                    <div className="font-medium">{plot.plotKey}</div>
                    {check && (
                      <div className="text-[11px] leading-tight text-warning-fg">
                        {plot.flags.length > 0 ? plot.flags[0] : derived.why}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-1.5 align-top">
                    <select
                      aria-label={`Facing of ${plot.plotKey}`}
                      value={final.facing || ''}
                      onChange={(e) => setDraft(setDirectionEdit(draft, plot.plotKey, { facing: e.target.value || null }))}
                      className="w-full rounded-md border border-hairline px-2 py-1 text-sm">
                      <option value="">Not set</option>
                      {FACING_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  </td>
                  {SIDES.map((s) => (
                    <td key={s} className="px-3 py-1.5 align-top">
                      <NamesCell
                        label={`${SIDE_LABEL[s]} of ${plot.plotKey}`}
                        value={final[s]}
                        onChange={(names) => setDraft(setDirectionEdit(draft, plot.plotKey, { [s]: names }))}
                      />
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="mt-2 text-xs text-gray-500">
        Written on Create: each unit&apos;s facing, and the names beside each side (roads, neighbouring plots, hall, water).
        Anything already set on an existing unit is kept. Price-affecting location charges are never touched.
      </p>
    </Fold>
  );
};

export default ReraMapDirections;
